import { ReplicaTransportError, type ReplicaResponse } from '@w/db-turso-browser-adapter/worker'
import type { Tel } from '@w/tel'

type Transport = {
  sendRemote(bytes: Uint8Array, signal?: AbortSignal, tel?: Tel): Promise<ReplicaResponse>
  fetchRemote(bytes: Uint8Array, signal?: AbortSignal, tel?: Tel): Promise<ReplicaResponse>
}
export type TransportStatusDriver = {
  subscribeTransport?: (listener: (error: unknown) => void) => () => void
}

/** Attempts finish even when the native exchange retains its demand for retry. */
export function createReplicaTransportStatus() {
  const listeners = new Set<(error: unknown) => void>()
  return {
    subscribeTransport(this: void, listener: (error: unknown) => void) {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
    wrap<T extends Transport>(transport: T): T {
      async function request(kind: keyof Transport, bytes: Uint8Array, signal?: AbortSignal, tel?: Tel) {
        try {
          const result = await transport[kind](bytes, signal, tel)
          if (result instanceof Uint8Array) { notify(null); return result }
          return { async *[Symbol.asyncIterator]() {
            try { yield* result; notify(null) } catch (cause) { throw failed(cause, signal) }
          } }
        } catch (cause) {
          throw failed(cause, signal)
        }
      }
      function notify(error: unknown) { for (const listener of listeners) listener(error) }
      function failed(cause: unknown, signal?: AbortSignal) {
        const error = cause instanceof TypeError && !signal?.aborted ? new ReplicaTransportError(0, 'offline', 'Replica transport unavailable', cause) : cause
        if (!signal?.aborted) notify(error)
        return error
      }
      return { ...transport, sendRemote: (bytes, signal, tel) => request('sendRemote', bytes, signal, tel), fetchRemote: (bytes, signal, tel) => request('fetchRemote', bytes, signal, tel) }
    },
  }
}
